import { createContext, useContext, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import { gql } from 'graphql-tag';
import { subDays, format } from 'date-fns';

// GraphQL query to get the count of unique domains with unviewed FFUF results
// This counts domains rather than individual endpoints to match the card grouping in the UI
const GET_FFUF_COUNT = gql`
  query GetFfufCount($startDate: timestamp!, $endDate: timestamp!) {
    live_domains_aggregate(
      where: {
        domain_endpoints: {
          viewed_at: {_is_null: true},
          times: {_gte: $startDate, _lte: $endDate}
        }
      }
    ) {
      aggregate {
        count
      }
    }
  }
`;

export const FfufCountContext = createContext(null);

export function FfufCountProvider({ children }) {
  const [count, setCount] = useState(0);
  
  // Memoize dates - Calculate endDate only once on mount
  const startDate = useMemo(() => 
    format(subDays(new Date(), 30), 'yyyy-MM-dd'), 
  []);
  const [endDate] = useState(() =>
    format(new Date(), 'yyyy-MM-dd HH:mm:ss') // Calculate only on initial render
  );

  // Updated query config
  const { data, loading, refetch } = useQuery(GET_FFUF_COUNT, {
    variables: { startDate, endDate },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    pollInterval: 600000,
    notifyOnNetworkStatusChange: false,
    onError: (error) => console.error('Error fetching FFUF count:', error)
  });

  // Optimized useEffect
  useEffect(() => {
    const newCount = data?.live_domains_aggregate?.aggregate?.count || 0;
    setCount(prev => prev !== newCount ? newCount : prev);
  }, [data]);

  // Use useMemo to memoize the context value
  const contextValue = useMemo(() => ({ 
    count, 
    setCount, 
    loading, 
    refetch 
  }), [count, loading, refetch]);

  return (
    <FfufCountContext.Provider value={contextValue}>
      {children}
    </FfufCountContext.Provider>
  );
}

FfufCountProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useFfufCount() {
  const context = useContext(FfufCountContext);
  if (!context) {
    throw new Error('useFfufCount must be used within a FfufCountProvider');
  }
  return context;
} 